import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, Output, EventEmitter, ChangeDetectorRef, effect, NgZone, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, switchMap, catchError, of } from 'rxjs';
import { IconService } from 'src/app/ui/icon.service';
import { NavItem, ThemeItem } from '../../../models/navigation.model';
import { MenuItem } from 'primeng/api';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { ThemeService } from 'src/app/services/theme.service';
import { ConfigService } from 'src/app/config.service';
import { SearchService, SearchResult, TripSearchResult } from 'src/app/api/search.service';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { EditNoteComponentInputs } from 'src/app/component-modules/notes/edit-note/edit-note.component';
import { EditScheduledActivityComponentInputs } from 'src/app/component-modules/notes/edit-scheduled-activity/edit-scheduled-activity.component';
import { TableExportService } from 'src/app/ui/tables/table-export.service';
import { UrlService } from 'src/app/services/url.service';

@Component({
  selector: 'app-mobile-nav',
  templateUrl: './mobile-nav.component.html',
  styleUrl: './mobile-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MobileNavComponent implements AfterViewInit, OnDestroy {

  private ngUnsubscribe = new Subject<void>();

  @Input({ required: true }) navItems: NavItem[] = [];
  @Input({ required: false }) accountMenuItems: MenuItem[] = [];
  @Input({ required: false }) username: string = '';
  @Input({ required: false }) themeItem?: ThemeItem;
  @Output() closeOtherMenus = new EventEmitter<void>();

  showMobileNav: boolean = false;
  activeNavItem: NavItem | undefined;
  secondaryNavItems: NavItem[] | undefined;

  // Search properties
  searchValue: string = '';
  searchResults: SearchResult[] = [];
  isSearchLoading: boolean = false;
  showMobileSearch: boolean = false;
  searchError: string | null = null;
  hasSearched: boolean = false;
  expandedResultCards: Set<string> = new Set();

  // Search subject for debounced search
  private searchSubject = new Subject<string>();

  // State for nested menu management
  expandedMenuItems: Set<string> = new Set();
  expandedSubMenuItems: { [key: string]: Set<string> } = {};

  // Account and theme state
  showAccountMenu: boolean = false;
  showThemePicker: boolean = false;
  isAccountMenuOpen: boolean = false;
  selectedPalette: string = '';
  selectedFont: string = '';

  // User photo properties
  userPhotoUrl: string | null = null;
  showUserPhoto: boolean = false;

  // Context menu state
  contextMenuItems: MenuItem[] = [];
  contextMenuTarget: NavItem | MenuItem | null = null;
  @ViewChild('contextMenu') contextMenu: any;

  constructor(
    public icons: IconService,
    private router: Router,
    public auth: LocalAuthService,
    public cdr: ChangeDetectorRef,
    private themeService: ThemeService,
    private configService: ConfigService,
    private searchService: SearchService,
    private ngZone: NgZone,
    private dataPadService: DataPadService,
    public tableExport: TableExportService,
    private urlService: UrlService
  ) {
    router.events
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((val) => {
        if (this.showMobileNav) {
          this.toggleMobileNav();
        }
      });

    // Effect to sync dropdown with theme service state
    effect(() => {
      const currentTheme = this.themeService.currentTheme();
      const currentFont = this.themeService.fontFamily();

      // Update selected palette (use theme name for dropdown)
      this.selectedPalette = currentTheme;

      // Update selected font
      this.selectedFont = currentFont;

      // Trigger change detection
      this.cdr.detectChanges();
    });

    // Set up debounced search (same as desktop search)
    this.searchSubject.pipe(
      switchMap(searchTerm => {
        if (!searchTerm || searchTerm.trim().length === 0) {
          this.searchResults = [];
          this.isSearchLoading = false;
          this.searchError = null;
          this.hasSearched = false;
          this.cdr.detectChanges();
          return of([]);
        }

        this.isSearchLoading = true;
        this.searchError = null;
        this.hasSearched = true;
        this.cdr.detectChanges();

        return this.searchService.search(searchTerm).pipe(
          catchError(error => {
            console.error('Search error:', error);
            this.ngZone.run(() => {
              this.searchError = 'Search failed. Please try again.';
              this.isSearchLoading = false;
              this.cdr.markForCheck();
            });
            return of([]);
          })
        );
      })
    ).subscribe(results => {



      if (results.length === 1 && results[0].type === 'Trip') {
        this.navigateToTrip(results[0] as TripSearchResult)

      }
      else {
        this.searchResults = results;
        this.hasSearched = true;
        this.isSearchLoading = false;
      }
      
      // Use detectChanges to ensure immediate UI update
      this.cdr.detectChanges();

    });
  }

  ngAfterViewInit(): void {
    this.initializeUserPhoto();
    this.initializeThemeDefaults();
  }

  private initializeThemeDefaults(): void {
    // Initialize from theme service
    this.selectedPalette = this.themeService.currentTheme();
    this.selectedFont = this.themeService.fontFamily();
  }

  private initializeUserPhoto(): void {
    const user = this.auth.currentUser;

    if (user && user.newstaffId) {
      // Use the proper base URL from environment configuration
      const staffPhotoBaseUrl = this.configService.staffPhotoUrl;
      const photoUrl = `${staffPhotoBaseUrl}${user.newstaffId}.jpg?no_cache=${Date.now()}`;

      // Test if the image exists by creating an Image object
      const img = new Image();
      img.onload = () => {
        this.userPhotoUrl = photoUrl;
        this.showUserPhoto = true;
        this.cdr.detectChanges();
      };
      img.onerror = () => {
        // Photo doesn't exist, fall back to avatar
        this.userPhotoUrl = null;
        this.showUserPhoto = false;
        this.cdr.detectChanges();
      };
      img.src = photoUrl;
    } else {
      this.userPhotoUrl = null;
      this.showUserPhoto = false;
    }
  }

  setTheme(theme?: string) {
    if (theme) {
      document.body.setAttribute('data-theme', theme);
    }
  }

  toggleMobileNav() {
    this.showMobileNav = !this.showMobileNav;
  }

  toggleMobileSearch() {
    this.showMobileSearch = !this.showMobileSearch;

    if (this.showMobileSearch) {
      // Close other menus when opening search
      this.showAccountMenu = false;
      this.showThemePicker = false;
      this.isAccountMenuOpen = false;
      this.activeNavItem = undefined;
    }

    this.cdr.detectChanges();
  }

  closeMobileSearch() {
    this.showMobileSearch = false;
  }

  setActiveNavItem(navItem: NavItem) {
    this.activeNavItem = navItem;

    // Close other menus when selecting a regular nav item
    this.showMobileSearch = false;
    this.showAccountMenu = false;
    this.showThemePicker = false;
    this.isAccountMenuOpen = false;

    // Handle external URL navigation
    if (navItem && navItem.url) {
      this.navigateToExternalUrl(navItem.url);
      this.closeMobileNav();
      return;
    }

    // Handle router link navigation
    if (navItem && navItem.routerLink) {
      this.router.navigate([navItem.routerLink]);
      this.closeMobileNav();
      return;
    }

    // Handle sub-items
    if (navItem && navItem.hasSubItems) {
      this.secondaryNavItems = navItem.subItems;
    }

    if (this.showMobileSearch) {
      this.toggleMobileSearch();
    }
  }

  // Get menu items from nav item (similar to desktop nav)
  getMenuItemsFromNavItem(navItem: NavItem): MenuItem[] {
    if (navItem._cachedMenuItems) {
      return navItem._cachedMenuItems;
    }

    let menuItems: MenuItem[] = [];

    if (navItem.menuItems && navItem.menuItems.length > 0) {
      menuItems = navItem.menuItems
        .filter(item => this.hasAccessToMenuItem(item)) // Filter based on roles
        .map(item => {
          const processedItem: MenuItem = {
            ...item,
            command: item.routerLink ? () => {
              this.router.navigate([item.routerLink!]);
              this.closeMobileNav();
            } : item.url ? () => {
              this.navigateToExternalUrl(item.url!);
              this.closeMobileNav();
            } : item.command,
            label: item.url ? `${item.label}` : item.label
          };

          if (item.icon && typeof item.icon === 'string') {
            processedItem.icon = item.icon;
          } else {
            delete processedItem.icon;
          }

          return processedItem;
        });
    }

    navItem._cachedMenuItems = menuItems;
    return menuItems;
  }

  hasAccessToMenuItem(item: any): boolean {
    // If no roles are specified, the item is accessible to everyone
    if (!item.roles || !Array.isArray(item.roles) || item.roles.length === 0) {
      return true;
    }

    // Check if user has any of the required roles
    return this.auth.hasAnyRole(item.roles);
  }

  // Toggle menu item expansion
  toggleMenuItemExpansion(menuItem: MenuItem, parentKey?: string) {
    const label = menuItem.label || '';
    const key = parentKey ? `${parentKey}-${label}` : label;

    if (this.expandedMenuItems.has(key)) {
      this.expandedMenuItems.delete(key);
    } else {
      this.expandedMenuItems.add(key);
    }
  }

  // Check if menu item is expanded
  isMenuItemExpanded(menuItem: MenuItem, parentKey?: string): boolean {
    const label = menuItem.label || '';
    const key = parentKey ? `${parentKey}-${label}` : label;
    return this.expandedMenuItems.has(key);
  }

  // Toggle submenu item expansion
  toggleSubMenuItemExpansion(menuItem: MenuItem, parentKey: string) {
    const label = menuItem.label || '';
    const key = `${parentKey}-${label}`;

    if (!this.expandedSubMenuItems[parentKey]) {
      this.expandedSubMenuItems[parentKey] = new Set();
    }

    if (this.expandedSubMenuItems[parentKey].has(label)) {
      this.expandedSubMenuItems[parentKey].delete(label);
    } else {
      this.expandedSubMenuItems[parentKey].add(label);
    }
  }

  // Check if submenu item is expanded
  isSubMenuItemExpanded(menuItem: MenuItem, parentKey: string): boolean {
    const label = menuItem.label || '';
    return this.expandedSubMenuItems[parentKey]?.has(label) || false;
  }

  // Handle menu item clicks
  handleMenuItemClick(item: MenuItem, event: Event) {
    event.preventDefault();
    event.stopPropagation();

    // If item has nested items, toggle expansion instead of navigating
    if (item.items && item.items.length > 0) {
      return; // Let the toggle method handle this
    }

    // Close mobile nav when navigating
    this.closeMobileNav();

    if (item.url) {
      this.navigateToExternalUrl(item.url);
      return;
    }

    if (item.routerLink) {
      this.router.navigate([item.routerLink]);
      return;
    }

    if (item.command && typeof item.command === 'function') {
      item.command({ item, originalEvent: event });
      return;
    }
  }

  // Navigate to external URL
  navigateToExternalUrl(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // Close mobile navigation
  closeMobileNav() {
    this.showMobileNav = false;
    this.activeNavItem = undefined;
    this.showMobileSearch = false;
    this.showAccountMenu = false;
    this.showThemePicker = false;
    this.isAccountMenuOpen = false;
    this.expandedMenuItems.clear();
    this.expandedSubMenuItems = {};
  }

  // Account menu handling
  handleAccountClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isAccountMenuOpen) {
      this.isAccountMenuOpen = false;
      this.showAccountMenu = false;
      this.cdr.detectChanges();
      return;
    }

    // Close other menus and reset state
    this.showThemePicker = false;
    this.showMobileSearch = false;
    this.activeNavItem = undefined;

    this.signInClicked();

    if (this.auth.currentUser) {
      this.isAccountMenuOpen = true;
      this.showAccountMenu = true;
    }

    this.cdr.detectChanges();
  }

  signInClicked() {
    if (this.auth.currentUser) {
      this.toggleAccountMenu();
    } else {
      this.toggleLoginModal();
    }
  }

  toggleAccountMenu() {
    this.showAccountMenu = !this.showAccountMenu;
  }

  toggleLoginModal() {
    // This would need to be implemented based on your login modal system
  }

  onAccountMenuHide() {
    this.isAccountMenuOpen = false;
    this.showAccountMenu = false;
  }

  // Theme picker handling
  handleThemeClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.showThemePicker) {
      this.showThemePicker = false;
      this.cdr.detectChanges();
      return;
    }

    // Close other menus and reset state
    this.showAccountMenu = false;
    this.isAccountMenuOpen = false;
    this.showMobileSearch = false;
    this.activeNavItem = undefined;

    this.showThemePicker = true;
    this.cdr.detectChanges();
  }

  onThemePickerHide() {
    this.showThemePicker = false;
  }

  toggleDarkMode = () => {
    this.themeService.toggleDarkMode();
  };

  onPaletteChange(e: any) {
    if (e?.value) {
      this.themeService.setTheme(e.value);
      this.selectedPalette = e.value;
    }
  }

  onFontChange(e: any) {
    if (e?.value) {
      this.themeService.setFontFamily(e.value);
      this.selectedFont = e.value;
    }
  }



  // Search methods (matching desktop search behavior)
  onSearchInputChange(event: any) {
    const query = event.target.value;
    this.searchValue = query;

    // Clear search results when input changes (no auto-search)
    this.searchResults = [];
    this.searchError = null;
    this.hasSearched = false;
    this.isSearchLoading = false;
    this.cdr.detectChanges();
  }

  onSearchEnterKeyPress() {
    if (!this.searchValue || this.searchValue.trim().length === 0) {
      return;
    }

    this.hasSearched = true;
    this.isSearchLoading = true;
    this.searchError = null;
    this.searchResults = []; // Clear previous results

    // Trigger search when Enter key is pressed
    this.searchSubject.next(this.searchValue);
    this.cdr.detectChanges();
  }

  onSearchButtonClick() {
    if (!this.searchValue || this.searchValue.trim().length === 0) {
      return;
    }

    this.hasSearched = true;
    this.isSearchLoading = true;
    this.searchError = null;
    this.searchResults = []; // Clear previous results

    // Trigger search with current search value
    this.searchSubject.next(this.searchValue);
    this.cdr.detectChanges();
  }

  onSearchResultClick(result: SearchResult) {
    // Navigate to the appropriate page based on result type
    if (result.type === 'Trip') {
      // For trips, navigate to trips page with group ID
      this.navigateToTrip(result)
    }
    // For contact cards, the link will be handled by the anchor tag in the template
  }

  navigateToTrip(result: TripSearchResult) {
    this.router.navigateByUrl(this.urlService.getContactCardUrl(result.groupId, { angularRoute: true, tripId: result.tripId }));

    // Close mobile nav after navigation
    this.showMobileNav = false;
    this.showMobileSearch = false;
    this.searchValue = '';
    this.hasSearched = false;
    this.searchResults = [];
    this.isSearchLoading = false;

    this.cdr.detectChanges();
  }

  // Toggle result card expansion
  toggleResultCard(result: SearchResult, index: number) {
    const cardId = `${result.type}-${result.groupId}-${result.name}-${index}`;
    if (this.expandedResultCards.has(cardId)) {
      this.expandedResultCards.delete(cardId);
    } else {
      this.expandedResultCards.add(cardId);
    }
    this.cdr.detectChanges();
  }

  // Check if result card is expanded
  isResultCardExpanded(result: SearchResult, index: number): boolean {
    const cardId = `${result.type}-${result.groupId}-${result.name}-${index}`;
    return this.expandedResultCards.has(cardId);
  }

  // Get unique key for result card (trackBy function)
  getResultCardId(index: number, result: SearchResult): string {
    return `${result.type}-${result.groupId}-${result.name}-${index}`;
  }

  openLogActivity(result: SearchResult) {
    const inputData: EditNoteComponentInputs = {
      custId: result.groupId,
      tripDetails: [],
      contacts: []
    };

    // Hide mobile nav when opening datapad
    if (this.showMobileNav) {
      this.toggleMobileNav();
    }

    this.dataPadService.showPanel<EditNoteComponentInputs>({
      panelName: 'editNote',
      inputData: inputData
    });
  }

  openScheduleActivity(result: SearchResult) {
    const inputData: EditScheduledActivityComponentInputs = {
      custId: result.groupId,
      tripDetails: [],
      contacts: []
    };

    // Hide mobile nav when opening datapad
    if (this.showMobileNav) {
      this.toggleMobileNav();
    }

    this.dataPadService.showPanel<EditScheduledActivityComponentInputs>({
      panelName: 'editScheduledActivity',
      inputData: inputData
    });
  }

  // Get chip styling class based on result type
  getTypeChipClass(type: string): string {
    switch (type) {
      case 'Group':
        return 'p-chip-info';
      case 'Participant':
        return 'p-chip-success';
      case 'Contact':
        return 'p-chip-warning';
      case 'Trip':
        return 'p-chip-help';
      default:
        return 'p-chip-secondary';
    }
  }


  // Hover event handlers for theme-aware styling
  onMouseEnter(event: Event) {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.setProperty('background-color', 'var(--p-surface-hover)');
    }
  }

  onMouseLeave(event: Event) {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.setProperty('background-color', 'var(--p-surface-0)');
    }
  }

  onMouseEnterTransparent(event: Event) {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.setProperty('background-color', 'var(--p-surface-hover)');
    }
  }

  onMouseLeaveTransparent(event: Event) {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.setProperty('background-color', 'transparent');
    }
  }

  // Export search results to CSV
  exportSearchResultsToCSV() {
    if (!this.searchResults || this.searchResults.length === 0) {
      return;
    }

    // Convert search results to CSV format
    const csvData = this.searchResults.map(result => ({
      'Type': result.type,
      'Name': result.name,
      'Group ID': result.groupId,
      'Company': result.company || '',
      'Phone 1': result.phone1 || '',
      'Phone 2': result.phone2 || '',
      'Email': result.email || '',
    }));

    // Generate CSV content
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header => {
          const value = row[header as keyof typeof row];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `search-results-${timestamp}.csv`;

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Context menu methods
  onNavItemRightClick(event: MouseEvent, navItem: NavItem) {
    event.preventDefault();
    event.stopPropagation();
    
    // Only show context menu for items that have a URL or routerLink
    if (!navItem.url && !navItem.routerLink) {
      return;
    }
    
    this.contextMenuTarget = navItem;
    this.contextMenuItems = this.getContextMenuItemsForNavItem(navItem);
    
    if (this.contextMenu && this.contextMenuItems.length > 0) {
      this.contextMenu.show(event);
    }
  }

  onMenuItemRightClick(event: MouseEvent, menuItem: MenuItem) {
    event.preventDefault();
    event.stopPropagation();
    
    // Only show context menu for items that have a URL or routerLink
    if (!menuItem.url && !menuItem.routerLink) {
      return;
    }
    
    this.contextMenuTarget = menuItem;
    this.contextMenuItems = this.getContextMenuItemsForMenuItem(menuItem);
    
    if (this.contextMenu && this.contextMenuItems.length > 0) {
      this.contextMenu.show(event);
    }
  }

  private getBasePath(): string {
    const currentPath = window.location.pathname;
    // Find the base path by looking for '/incident-management' in the current path
    const basePathMatch = currentPath.match(/^(\/incident-management)/);
    return basePathMatch ? basePathMatch[1] : '';
  }

  getContextMenuItemsForNavItem(navItem: NavItem): MenuItem[] {
    const items: MenuItem[] = [];
    
    if (navItem.routerLink) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([navItem.routerLink], { queryParams: navItem.queryParams })
      );
      const fullUrl = this.configService.baseUrl + url;
      
      items.push({
        label: 'Open in New Tab',
        icon: 'pi pi-external-link',
        command: () => {
          window.open(fullUrl, '_blank', 'noopener,noreferrer');
        }
      });
      
      items.push({
        label: 'Open in New Window',
        icon: 'pi pi-window-maximize',
        command: () => {
          window.open(fullUrl, '_blank', 'width=1200,height=800,noopener,noreferrer');
        }
      });
      
      items.push({
        separator: true
      });
      
      items.push({
        label: 'Copy Link',
        icon: 'pi pi-copy',
        command: () => {
          const fullUrlWithBase = this.configService.baseUrl + url;
          this.copyToClipboard(fullUrlWithBase);
        }
      });
    } else if (navItem.url) {
      items.push({
        label: 'Open in New Tab',
        icon: 'pi pi-external-link',
        command: () => {
          window.open(navItem.url!, '_blank', 'noopener,noreferrer');
        }
      });
      
      items.push({
        label: 'Open in New Window',
        icon: 'pi pi-window-maximize',
        command: () => {
          window.open(navItem.url!, '_blank', 'width=1200,height=800,noopener,noreferrer');
        }
      });
      
      items.push({
        separator: true
      });
      
      items.push({
        label: 'Copy Link',
        icon: 'pi pi-copy',
        command: () => {
          this.copyToClipboard(navItem.url!);
        }
      });
    }
    
    return items;
  }

  getContextMenuItemsForMenuItem(menuItem: MenuItem): MenuItem[] {
    const items: MenuItem[] = [];
    
    if (menuItem.routerLink) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([menuItem.routerLink], { queryParams: menuItem.queryParams })
      );
      const fullUrl = this.configService.baseUrl + url;
      
      items.push({
        label: 'Open in New Tab',
        icon: 'pi pi-external-link',
        command: () => {
          window.open(fullUrl, '_blank', 'noopener,noreferrer');
        }
      });
      
      items.push({
        label: 'Open in New Window',
        icon: 'pi pi-window-maximize',
        command: () => {
          window.open(fullUrl, '_blank', 'width=1200,height=800,noopener,noreferrer');
        }
      });
      
      items.push({
        separator: true
      });
      
      items.push({
        label: 'Copy Link',
        icon: 'pi pi-copy',
        command: () => {
          const fullUrlWithBase = this.configService.baseUrl + url;
          this.copyToClipboard(fullUrlWithBase);
        }
      });
    } else if (menuItem.url) {
      items.push({
        label: 'Open in New Tab',
        icon: 'pi pi-external-link',
        command: () => {
          window.open(menuItem.url!, '_blank', 'noopener,noreferrer');
        }
      });
      
      items.push({
        label: 'Open in New Window',
        icon: 'pi pi-window-maximize',
        command: () => {
          window.open(menuItem.url!, '_blank', 'width=1200,height=800,noopener,noreferrer');
        }
      });
      
      items.push({
        separator: true
      });
      
      items.push({
        label: 'Copy Link',
        icon: 'pi pi-copy',
        command: () => {
          this.copyToClipboard(menuItem.url!);
        }
      });
    }
    
    return items;
  }

  private async copyToClipboard(text: string): Promise<void> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Failed to copy text:', err);
        }
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
